import { formatMoney } from '@shopify/theme-currency';

class KlaviyoBundleTracker {
  constructor() {
    this.hasTriggeredFlow = false;
    this.debounceTimer = null;
    this.debounceDelay = 8000;

    document.addEventListener('bundle:interaction', (e) => this.onInteraction(e));
  }

  onInteraction(event) {
    this.debouncedSync(event.detail);
  }

  debouncedSync(detail) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.syncProfileProperties(detail);

      if (!this.hasTriggeredFlow) {
        this.triggerFlow();
        this.hasTriggeredFlow = true;
      }
    }, this.debounceDelay);
  }

  syncProfileProperties(data) {
    if (typeof klaviyo === 'undefined') return;

    const selections = data.selections || {};
    const actualSelections = Object.entries(selections).filter(([, sel]) => sel !== null);
    if (actualSelections.length < 2) return;

    const properties = this.buildProfileProperties(data);
    klaviyo.identify(properties);
  }

  triggerFlow() {
    if (typeof klaviyo === 'undefined') return;
    klaviyo.track('Bundle Configurator Started');
  }

  buildProfileProperties(data) {
    const settings = window.klaviyoBundleSettings || {};
    const bundleSettings = window.pillowConfiguratorSettings || {};
    const selections = data.selections || {};
    const actualSelections = Object.entries(selections).filter(([, sel]) => sel !== null);
    const itemCount = actualSelections.length;

    const subtotal = actualSelections.reduce((sum, [, s]) => sum + (s.price || 0), 0);
    const discountPercent = itemCount >= 3 ? bundleSettings.discount3 || 0 : itemCount === 2 ? bundleSettings.discount2 || 0 : 0;
    const discountedTotal = Math.round(subtotal * (1 - discountPercent / 100));

    const email = settings.customerEmail;

    const properties = {
      ...(email && { $email: email }),
      Bundle_Name: settings.bundleName || 'Pillow Configurator',
      Bundle_Step: data.state?.currentStep || 1,
      Bundle_Total_Items: itemCount,
      Bundle_Subtotal: this.formatPrice(subtotal),
      Bundle_Discount_Percent: discountPercent,
      Bundle_Discounted_Total: this.formatPrice(discountedTotal),
      Bundle_URL: window.location.href,
      Bundle_Updated_At: new Date().toISOString(),
    };

    // Flatten each selection into individual profile properties
    actualSelections.forEach(([step, sel]) => {
      const prefix = `Bundle_Product${step}`;
      properties[`${prefix}_Name`] = sel.title;
      properties[`${prefix}_Variant`] = sel.variantTitle;
      properties[`${prefix}_Price`] = this.formatPrice(sel.price);
      properties[`${prefix}_Image`] = this.formatImageUrl(sel.image);
      properties[`${prefix}_ID`] = sel.productId;
      properties[`${prefix}_Variant_ID`] = sel.variantId;
    });

    // Clear properties for steps with no selection (e.g. "none")
    const totalSteps = data.config?.steps?.length || 3;
    for (let i = 1; i <= totalSteps; i++) {
      if (!selections[i]) {
        const prefix = `Bundle_Product${i}`;
        properties[`${prefix}_Name`] = '';
        properties[`${prefix}_Variant`] = '';
        properties[`${prefix}_Price`] = '';
        properties[`${prefix}_Image`] = '';
        properties[`${prefix}_ID`] = '';
        properties[`${prefix}_Variant_ID`] = '';
      }
    }

    return properties;
  }

  formatPrice(cents) {
    if (!cents) return null;
    const moneyFormat = window.pillowConfiguratorSettings?.moneyFormat || '€{{amount_with_comma_separator}}';
    return formatMoney(cents, moneyFormat);
  }

  formatImageUrl(url) {
    if (!url) return '';
    if (url.startsWith('//')) return 'https:' + url;
    return url;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new KlaviyoBundleTracker());
} else {
  new KlaviyoBundleTracker();
}
